'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

interface Message {
	icon: string;
	name: string;
	text: string;
	time: string;
	verified?: boolean;
	id?: number;
}

type StrapiMessage = NonNullable<
	components['schemas']['DynamicZoneTwoColumnMessagePreviewComponent']['messages']
>[number];

function normalizeMessages(messages: StrapiMessage[] | undefined): Message[] {
	return (messages ?? []).map(message => ({
		icon: message.icon || '',
		name: message.name || '',
		text: message.text || '',
		time: message.time || '',
		verified: message.verified,
		id: message.id,
	}));
}

export default function TwoColumnMessagePreview({
	heading = 'Boost Trust with Logo Verified SMS',
	subHeading = "Stand out from the crowd with Logo Verified SMS, a feature that enhances your brand's credibility and customer trust. Your messages will display your brand name, logo, and a verified tick, ensuring instant recognition.",
	features = [],
	messages = [
		{
			icon: '🔒',
			name: 'SecureBox Enterprise',
			text: 'Your secure login code is: 347891. This code will expire in 5 minutes. Never share this code with anyone.',
			time: 'Just now',
			verified: true,
		},
		{
			icon: '😊',
			name: 'HappyShop',
			text: 'Thank you for your order #12345! Your package will arrive tomorrow between 2-4 PM. Track your delivery here.',
			time: '5 minutes ago',
			verified: true,
		},
	],
	backgroundColor = 'bg-white',
	checkmarkColor = 'bg-green-500',
	isFirst = false,
}: components['schemas']['DynamicZoneTwoColumnMessagePreviewComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	const [activeMessages, setActiveMessages] = useState<Message[]>(() =>
		normalizeMessages(messages),
	);
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		const normalizedMessages = normalizeMessages(messages);
		setActiveMessages(normalizedMessages);

		const showMessages = async (shouldReset: boolean) => {
			if (shouldReset) {
				setActiveMessages([]);
			}

			const startIndex = shouldReset ? 0 : normalizedMessages.length;

			for (let i = startIndex; i < normalizedMessages.length; i++) {
				await new Promise(resolve => setTimeout(resolve, 800));
				setActiveMessages(prev => [...prev, normalizedMessages[i]]);
			}
		};

		const animationInterval = setInterval(() => {
			showMessages(true);
		}, 10000);

		return () => clearInterval(animationInterval);
	}, [messages]);

	const messageVariants = {
		hidden: {
			opacity: 0,
			y: 20,
			scale: 0.95,
		},
		visible: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: {
				type: 'spring',
				stiffness: 100,
				damping: 15,
			},
		},
		hover: {
			scale: 1.02,
			y: -5,
			boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
			transition: {
				type: 'spring',
				stiffness: 400,
				damping: 25,
			},
		},
		exit: {
			opacity: 0,
			scale: 0.95,
			transition: {
				duration: 0.2,
			},
		},
	};

	const verifiedBadgeVariants = {
		hidden: { scale: 0, opacity: 0 },
		visible: {
			scale: 1,
			opacity: 1,
			transition: {
				delay: 0.2,
				type: 'spring',
				stiffness: 200,
			},
		},
	};

	const sectionVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				when: 'beforeChildren',
				staggerChildren: 0.2,
				duration: 0.5,
			},
		},
	};

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: {
			y: 0,
			opacity: 1,
			transition: {
				type: 'spring',
				stiffness: 80,
				damping: 12,
			},
		},
	};

	const checkmarkVariants = {
		hidden: { scale: 0 },
		visible: {
			scale: 1,
			transition: {
				type: 'spring',
				stiffness: 300,
				damping: 20,
				delay: 0.1,
			},
		},
	};

	return (
		<motion.section
			variants={sectionVariants}
			initial="hidden"
			animate={isVisible ? 'visible' : 'hidden'}
			className={cn(
				'two-column-message-preview-section px-4 py-4 sm:px-6 lg:px-8',
				backgroundColor,
			)}>
			<noscript>
				<style>{`
					.two-column-message-preview-section h2[style],
					.two-column-message-preview-section h3[style],
					.two-column-message-preview-section p[style],
					.two-column-message-preview-section div[style*='opacity'],
					.two-column-message-preview-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				{heading && (
					<motion.div
						variants={itemVariants}
						className="mx-auto mb-8 max-w-3xl text-center">
						<SectionHeading className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl lg:text-4xl">
							{heading}
						</SectionHeading>
					</motion.div>
				)}

				<div
					className={cn(
						'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12',
						'items-start',
					)}>
					<motion.div
						variants={itemVariants}
						className="flex flex-col justify-center">
						{subHeading && (
							<motion.p
								variants={itemVariants}
								className="mb-6 text-base leading-relaxed text-gray-600 sm:text-lg">
								{subHeading}
							</motion.p>
						)}
						<motion.div
							variants={itemVariants}
							className="space-y-3">
							{features.map((feature, index) => (
								<motion.div
									key={index}
									variants={itemVariants}
									custom={index}
									className="group flex items-start gap-3"
									whileHover={{
										x: 5,
										transition: {
											type: 'spring',
											stiffness: 300,
										},
									}}
									whileTap={{ scale: 0.98 }}>
									<div className="mt-1 flex-shrink-0">
										<motion.div
											variants={checkmarkVariants}
											whileHover={{ scale: 1.2 }}
											className={cn(
												'flex h-5 w-5 items-center justify-center rounded-full shadow-sm transition-all duration-200 group-hover:shadow-md',
												checkmarkColor,
											)}>
											<svg
												className="h-3 w-3 text-white"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
												aria-hidden="true">
												<motion.path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M5 13l4 4L19 7"
													initial={{ pathLength: 0 }}
													animate={{ pathLength: 1 }}
													transition={{
														delay:
															0.2 + index * 0.1,
														duration: 0.5,
														ease: 'easeInOut',
													}}
												/>
											</svg>
										</motion.div>
									</div>
									<div>
										<motion.p
											className="text-base leading-relaxed text-gray-700 transition-colors duration-200 group-hover:text-gray-900"
											whileHover={{ fontWeight: 500 }}>
											{feature.text}
										</motion.p>
									</div>
								</motion.div>
							))}
						</motion.div>
					</motion.div>
					<motion.div
						variants={itemVariants}
						className="relative flex min-h-[400px] items-start">
						<div className="w-full space-y-3 overflow-hidden pt-0">
							<AnimatePresence mode="popLayout">
								{activeMessages.map((message, index) => (
									<motion.div
										key={index}
										layout
										variants={messageVariants}
										initial="hidden"
										animate="visible"
										exit="exit"
										whileHover="hover"
										className="cursor-pointer rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-shadow duration-300"
										style={{ originX: 0.5, originY: 0.5 }}>
										<div className="mb-2 flex items-center gap-3">
											<motion.span
												className="text-2xl"
												initial={{
													scale: 0,
													rotate: -10,
												}}
												animate={{
													scale: 1,
													rotate: 0,
												}}
												transition={{
													delay: 0.1,
													type: 'spring',
													stiffness: 200,
												}}>
												{message.icon}
											</motion.span>
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<h3 className="font-medium text-gray-900">
														{message.name}
													</h3>
													{message.verified && (
														<motion.span
															variants={
																verifiedBadgeVariants
															}
															initial="hidden"
															animate="visible"
															className="inline-flex items-center rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
															✓ Verified
														</motion.span>
													)}
												</div>
												<p className="text-sm text-gray-500">
													{message.time}
												</p>
											</div>
										</div>
										<motion.p
											initial={{ opacity: 0 }}
											animate={{ opacity: 1 }}
											transition={{ delay: 0.3 }}
											className="text-gray-700">
											{message.text}
										</motion.p>
									</motion.div>
								))}
							</AnimatePresence>
						</div>
					</motion.div>
				</div>
			</div>
		</motion.section>
	);
}
